import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { STRIPE_PRICES, goToCustomerPortal } from '../config/stripeUrls'

const PLANS = [
  {
    id: 'full',
    name: 'Full Access',
    tag: 'FULL ACCESS',
    description: 'The complete arsenal. Every edge, every tool.',
    color: '#f59e0b',
    rgb: '245,158,11',
    highlight: true,
    desktopOrder: 3,
    prices: [
      { label: 'Weekly', amount: '34.99', period: 'wk', priceId: STRIPE_PRICES.full_access_weekly, trial: true },
      { label: 'Monthly', amount: '109.99', period: 'mo', priceId: STRIPE_PRICES.full_access_monthly, wasWeekly: '151.62' },
    ],
    features: [
      'Full access to both Sports and Promo packages',
    ],
    trial: true,
  },
  {
    id: 'sports',
    name: 'Sports Premium',
    tag: 'SPORTS',
    description: 'The best non-promo betting tools on the market.',
    color: '#3b82f6',
    rgb: '59,130,246',
    desktopOrder: 1,
    prices: [
      { label: 'Weekly', amount: '19.99', period: 'wk', priceId: STRIPE_PRICES.sports_weekly },
      { label: 'Monthly', amount: '64.99', period: 'mo', priceId: STRIPE_PRICES.sports_monthly, wasWeekly: '86.62' },
    ],
    features: [
      'EV Software (+EV, Prop Outliers, Pick\'em EV)',
      'Arbitrage Software',
      'Middles Software',
      'Odds Comparison Software',
      'Bet Tracker',
      'Education Modules',
      'Bookmaker List',
      'Betting Calculators',
      'Premium Discord Access + Discord Bots',
      'Contact with our team for any questions',
      'Cancel Anytime',
    ],
    trial: false,
  },
  {
    id: 'promo',
    name: 'Promo Premium',
    tag: 'PROMO',
    description: 'Every promotional edge, in one place.',
    color: '#a855f7',
    rgb: '168,85,247',
    desktopOrder: 2,
    prices: [
      { label: 'Weekly', amount: '19.99', period: 'wk', priceId: STRIPE_PRICES.horse_racing_weekly },
      { label: 'Monthly', amount: '64.99', period: 'mo', priceId: STRIPE_PRICES.horse_racing_monthly, wasWeekly: '86.62' },
    ],
    features: [
      'Bonus Turnover Software',
      'Horse Racing Software',
      'Odds Comparison Software',
      'Bet Tracker',
      'Education Modules',
      'Bookmaker List',
      'Betting Calculators',
      'Premium Discord Access + Discord Bots',
      'Contact with our team for any questions',
      'Cancel Anytime',
    ],
    trial: false,
  },
]

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600&display=swap');

  .pricing-root {
    font-family: 'Barlow', sans-serif;
    background:
      radial-gradient(1000px 460px at 70% -12%, rgba(59, 130, 246, 0.12), transparent 65%),
      radial-gradient(700px 400px at 8% 20%, rgba(168, 85, 247, 0.05), transparent 60%),
      radial-gradient(800px 460px at 95% 105%, rgba(59, 130, 246, 0.05), transparent 60%),
      #010714;
    min-height: 100vh;
    padding: 0;
    position: relative;
    overflow: hidden;
  }

  .pricing-aurora {
    position: fixed;
    top: -22%;
    left: 50%;
    width: 900px;
    height: 620px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(59, 130, 246, 0.07), transparent);
    filter: blur(60px);
    pointer-events: none;
    z-index: 0;
    animation: pricingAurora 38s ease-in-out infinite alternate;
  }
  @keyframes pricingAurora {
    from { transform: translate(-80%, 0) rotate(0deg) scale(1); }
    to { transform: translate(-18%, 14%) rotate(35deg) scale(1.15); }
  }
  @media (prefers-reduced-motion: reduce) {
    .pricing-aurora { animation: none; }
  }



  .pricing-inner {
    position: relative;
    z-index: 1;
    max-width: 1140px;
    margin: 0 auto;
    padding: 60px 24px 80px;
  }

  .pricing-header {
    text-align: center;
    margin-bottom: 24px;
  }

  .pricing-title {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: clamp(40px, 6vw, 72px);
    font-weight: 900;
    color: #f1f5f9;
    line-height: 1;
    letter-spacing: -0.01em;
    text-transform: uppercase;
    margin: 0;
  }

  .pricing-sub {
    font-size: 16px;
    color: #64748b;
    font-weight: 400;
    max-width: 420px;
    margin: 0 auto;
    line-height: 1.6;
  }

  .pricing-trust {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-bottom: 32px;
  }

  .trust-item {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 8px 16px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 999px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
  }

  .pricing-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (min-width: 861px) {
    .plan-card { order: var(--desktop-order, 0); }
  }

  @media (max-width: 860px) {
    .pricing-cards {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  .plan-card {
    background: #0d1b2e;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 20px;
    padding: 32px 28px 36px;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 24px rgba(0,0,0,0.3);
  }

  .plan-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.4);
  }

  .plan-card.highlighted {
    background: #0f1e35;
    border-color: rgba(245,158,11,0.25);
    box-shadow: 0 4px 32px rgba(245,158,11,0.08), 0 0 0 1px rgba(245,158,11,0.1);
  }

  .plan-card.highlighted:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 48px rgba(245,158,11,0.12), 0 0 0 1px rgba(245,158,11,0.15);
  }

  .plan-accent-bar {
    position: absolute;
    top: 0;
    left: 24px;
    right: 24px;
    height: 2px;
    border-radius: 0 0 4px 4px;
  }

  .plan-tag {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .plan-tag-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    animation: pulse-dot 2s ease-in-out infinite;
  }

  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }

  .plan-popular-badge {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    background: #f59e0b;
    color: #060d1a;
    padding: 2px 8px;
    border-radius: 2px;
    margin-left: auto;
  }

  .plan-name {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 28px;
    font-weight: 800;
    color: #f1f5f9;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    line-height: 1;
    margin-bottom: 8px;
  }

  .plan-desc {
    font-size: 13px;
    color: #94a3b8;
    line-height: 1.5;
    margin-bottom: 28px;
    font-weight: 400;
  }

  /* One global billing toggle above the cards. Centred rather than full width
     so it reads as a page control, not as part of any one card. */
  .billing-toggle-row {
    display: flex;
    justify-content: center;
  }

  .billing-toggle {
    display: inline-flex;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px;
    padding: 4px;
    gap: 4px;
    margin: 0 auto 30px;
  }

  .billing-toggle-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 8px 20px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    transition: background 0.15s, color 0.15s;
    background: transparent;
    color: #64748b;
  }

  .billing-toggle-btn:hover {
    color: #cbd5e1;
  }

  .billing-toggle-btn.active {
    background: rgba(255,255,255,0.08);
    color: #f1f5f9;
  }

  /* Green because it is money saved. Stays visible on both states here: it
     labels what the Monthly option IS, rather than nagging you toward it. */
  .billing-toggle-save {
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(52,211,153,0.12);
    color: #34d399;
    font-size: 9.5px;
    font-weight: 800;
    letter-spacing: 0.06em;
    white-space: nowrap;
  }

  .plan-was {
    height: 16px;
    margin-bottom: 12px;
    font-size: 12.5px;
    line-height: 16px;
    color: #64748b;
    font-weight: 400;
  }

  .plan-was s {
    color: #475569;
  }

  .plan-price-row {
    margin-bottom: 6px;
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .plan-currency {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #64748b;
    line-height: 1;
    letter-spacing: 0.05em;
  }

  .plan-amount {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 56px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -0.02em;
  }

  .plan-period {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .plan-trial-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #f59e0b;
    background: rgba(245,158,11,0.08);
    border: 1px solid rgba(245,158,11,0.2);
    padding: 4px 10px;
    border-radius: 2px;
  }

  .plan-no-trial {
    font-size: 11px;
    color: #475569;
    font-family: 'Barlow Condensed', sans-serif;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .plan-cta {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    transition: opacity 0.15s, transform 0.1s;
    margin-bottom: 32px;
    position: relative;
    overflow: hidden;
  }

  .plan-cta:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .plan-cta:active:not(:disabled) {
    transform: translateY(0);
  }

  .plan-cta:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .plan-divider {
    height: 1px;
    background: rgba(255,255,255,0.05);
    margin-bottom: 24px;
  }

  .plan-features-label {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #475569;
    margin-bottom: 14px;
  }

  .plan-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
  }

  .plan-feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #cbd5e1;
    font-weight: 500;
  }

  .plan-feature-check {
    width: 16px;
    height: 16px;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .pricing-footer {
    margin-top: 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .pricing-manage-link {
    font-size: 12px;
    color: #334155;
    font-weight: 500;
  }

  .pricing-manage-link span {
    color: #3b82f6;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .pricing-error {
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.2);
    color: #f87171;
    font-size: 13px;
    padding: 10px 16px;
    border-radius: 3px;
    margin-bottom: 24px;
    font-family: 'Barlow Condensed', sans-serif;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

`

function PlanCard({ plan, onSubscribe, loading, billingIdx }) {

  const selected = plan.prices[billingIdx]
  const [amt, dec] = selected.amount.split('.')

  return (
    <div className={`plan-card${plan.highlight ? ' highlighted' : ''}`} style={{ '--desktop-order': plan.desktopOrder }}>
      <div className="plan-accent-bar" style={{ background: `linear-gradient(90deg, ${plan.color}, ${plan.color}88)` }} />

      <div className="plan-tag" style={{ color: plan.color }}>
        <div className="plan-tag-dot" style={{ background: plan.color }} />
        {plan.tag}
        {plan.highlight && <span className="plan-popular-badge">Most Popular</span>}
      </div>

      <div className="plan-name">{plan.name}</div>
      <div className="plan-desc">{plan.description}</div>

      <div style={{ marginBottom: '4px' }}>
        <span className="plan-currency">AUD $</span>
      </div>
      <div className="plan-price-row">
        <span className="plan-amount" style={{ color: plan.color }}>{amt}</span>
        <span className="plan-amount" style={{ color: `rgba(${plan.rgb},0.4)`, fontSize: '32px' }}>.{dec}</span>
        <span className="plan-period">/ {selected.period}</span>
      </div>

      {/* Weekly equivalent of the monthly price, so the saving is visible without
          the reader having to know a month is 52/12 weeks. Height is reserved on
          both cycles so the cards do not jump a line when the toggle flips. */}
      <div className="plan-was">
        {selected.wasWeekly && <><s>${selected.wasWeekly}</s> billed weekly</>}
      </div>

      <button
        className="plan-cta"
        onClick={() => onSubscribe(selected.priceId, !!selected.trial)}
        disabled={loading === selected.priceId}
        style={{
          background: plan.highlight
            ? `linear-gradient(135deg, #f59e0b, #fbbf24)`
            : `rgba(${plan.rgb}, 0.12)`,
          color: plan.highlight ? '#060d1a' : plan.color,
          border: plan.highlight ? 'none' : `1px solid rgba(${plan.rgb}, 0.3)`,
          marginBottom: '10px',
        }}
      >
        {loading === selected.priceId
          ? 'Redirecting...'
          : selected.trial ? 'Start Free Trial →' : 'Get Started →'}
      </button>

      <div style={{ height: '22px', marginBottom: '22px', display: 'flex', alignItems: 'center' }}>
        {selected.trial && (
          <div className="plan-trial-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            7-day free trial — cancel anytime
          </div>
        )}
        {!selected.trial && plan.trial && (
          <div className="plan-no-trial">No trial on monthly</div>
        )}
      </div>

      <div className="plan-divider" />
      <div className="plan-features-label">Includes</div>

      <div className="plan-features">
        {plan.features.map((f, i) => (
          <div key={i} className="plan-feature">
            <div className="plan-feature-check" style={{ background: `rgba(${plan.rgb},0.1)` }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={plan.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            {f}
          </div>
        ))}
      </div>
    </div>
  )
}


export default function PricingPage() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState(null)
  const [billingIdx, setBillingIdx] = useState(0)

  const handleSubscribe = async (priceId, hasTrial) => {
    if (!user?.email) {
      setError('Please sign in to subscribe.')
      return
    }
    setLoading(priceId)
    setError(null)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
        }
      })
      if (fnError) throw fnError
      if (data?.url) {
        window.location.href = data.url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError('Something went wrong. Please try again.')
      setLoading(null)
    }
  }

  return (
    <>
      <style>{css}</style>
      <div className="pricing-root">
        <div className="pricing-aurora" />

        <div className="pricing-inner">
          <div className="pricing-header">
            <h1 className="pricing-title">Choose Your <span style={{ color: '#f59e0b' }}>Plan</span></h1>
          </div>

          <div className="pricing-trust">
            <div className="trust-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Cancel Anytime
            </div>
            <div className="trust-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Stripe Secured
            </div>
            <div className="trust-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              7-Day Free Trial
            </div>
            <div className="trust-item">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Instant Access
            </div>
          </div>

          {error && <div className="pricing-error">{error}</div>}

          {/* One toggle for every card. The cycle is a property of the purchase,
              not of a plan, and a control per card invited comparing two plans on
              different cycles. */}
          <div className="billing-toggle-row">
          <div className="billing-toggle">
            {['Weekly', 'Monthly'].map((label, i) => (
              <button
                key={label}
                className={`billing-toggle-btn${billingIdx === i ? ' active' : ''}`}
                onClick={() => setBillingIdx(i)}
              >
                {label}
                {i === 1 && <span className="billing-toggle-save">Save 25%</span>}
              </button>
            ))}
          </div>
          </div>

          <div className="pricing-cards">
            {PLANS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onSubscribe={handleSubscribe} loading={loading} billingIdx={billingIdx} />
            ))}
          </div>

          <div className="pricing-footer">
            {!user && (
              <p className="pricing-manage-link">
                Already subscribed?{' '}
                <span onClick={goToCustomerPortal}>Manage your subscription</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
